import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CiEdit } from 'react-icons/ci';
import { fetchJewellersSidebarRequest } from '../../../redux/JewellerList/JewellerSlice';
import {
  getLuckyDrawBannersRequest,
  saveLuckyDrawBannerRequest,
  updateLuckyDrawBannerStatusRequest,
} from '../../../redux/LuckyDrawBanner/luckyDrawBannerSlice';
import TableCompo from '../Table/TableCompo';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import useCsvConverter from '../../../hooks/convertToCSV';

const getFileName = (path) => (path ? path.split('/').pop() : '-');

const triggerCsvDownload = (csvText, filename) => {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const LuckyDrawBanner = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const { jewellersSidebar = [] } = useSelector((state) => state.jeweller);
  const cdnUrl = useSelector(
    (state) => state?.appConfig?.configData?.cdnUrl || '',
  );
  const {
    bannerData = [],
    total = 0,
    loading,
    saving,
  } = useSelector((state) => state.luckyDrawBanner || {});

  const [targetType, setTargetType] = useState('All');
  const [selectedJewellers, setSelectedJewellers] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [existingFile, setExistingFile] = useState('');
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [jewellersOpen, setJewellersOpen] = useState(false);
  const [jewellersList, setJewellersList] = useState([]);
  const [jewellerSearch, setJewellerSearch] = useState('');

  const listParams = {
    page: page + 1,
    limit,
    ...(search ? { search } : {}),
  };

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest({ allActive: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getLuckyDrawBannersRequest(listParams));
  }, [dispatch, page, limit, search]);

  const jewellerOptions = [...jewellersSidebar].sort((a, b) =>
    (a.jewellerName || '').localeCompare(b.jewellerName || ''),
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setEditId(null);
    setTargetType('All');
    setSelectedJewellers([]);
    setSelectedFile(null);
    setFileName('');
    setPreviewUrl('');
    setExistingFile('');
  };

  const handleSave = () => {
    if (targetType === 'Custom' && !selectedJewellers.length) {
      toast.error('Please select jeweller');
      return;
    }
    if (!editId && !selectedFile) {
      toast.error('Please choose a file before saving');
      return;
    }

    const formData = new FormData();
    if (editId) formData.append('id', editId);
    formData.append('type', targetType);
    if (targetType === 'Custom') {
      formData.append('jewellerIds', JSON.stringify(selectedJewellers));
    }
    if (selectedFile) formData.append('files', selectedFile);

    dispatch(
      saveLuckyDrawBannerRequest({
        formData,
        listParams: editId ? listParams : { ...listParams, page: 1 },
      }),
    );

    if (!editId) setPage(0);
    resetForm();
  };

  const handleEdit = (row) => {
    setEditId(row._id);
    setTargetType(row.type || 'All');
    setSelectedJewellers(row.jewellerIds || []);
    setSelectedFile(null);
    setFileName(getFileName(row.files?.[0]));
    setPreviewUrl('');
    setExistingFile(row.files?.[0] || '');
  };

  const resolveFileUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = (cdnUrl || process.env.REACT_APP_API_BASE_URL || '').replace(
      /\/$/,
      '',
    );
    return `${base}/${String(path).replace(/^\//, '')}`;
  };

  const handleDownloadCsv = () => {
    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/jeweller-lucky-draw/getInstalaxmiBannersCsv',
        query: {},
        callback: (response) => {
          if (response?.error) {
            toast.error(response.message || 'Failed to download CSV');
            return;
          }
          const rows = Array.isArray(response?.data)
            ? response.data
            : response?.data?.data || [];
          const csv = convertToCsv(rows);
          if (!csv) {
            toast.error('No records to download');
            return;
          }
          triggerCsvDownload(csv, 'instalaxmi-banners.csv');
        },
      }),
    );
  };

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page);
        break;
      case 'changeRowsPerPage':
        setLimit(tableState.rowsPerPage);
        setPage(0);
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPage(0);
        break;
      default:
        break;
    }
  };

  const handleStatusClick = (row) => {
    setSelectedRow(row);
    setOpenConfirm(true);
  };

  const handleConfirm = () => {
    if (selectedRow) {
      dispatch(
        updateLuckyDrawBannerStatusRequest({
          id: selectedRow._id,
          status: !selectedRow.status,
        }),
      );
    }
    setOpenConfirm(false);
    setSelectedRow(null);
  };

  const handleCancel = () => {
    setOpenConfirm(false);
    setSelectedRow(null);
  };

  const tableRows = bannerData.map((row) => ({
    ...row,
    jewellerNames:
      row.type === 'All'
        ? 'All'
        : (row.jewellers || []).map((j) => j.jewellerName).join(', ') || '-',
    statusText: row.status ? 'Active' : 'Inactive',
    createdByName: row.createdBy?.name || '-',
    updatedByName: row.updatedBy?.name || '-',
    createdAtText: row.createdAt
      ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A')
      : '-',
  }));

  const columns = [
    { field: 'createdAtText', headerName: 'Created At', minWidth: 170 },
    { field: 'type', headerName: 'Type', minWidth: 120 },
    {
      field: 'jewellerNames',
      headerName: 'Jewellers',
      minWidth: 260,
      render: (val, row) => {
        const jewellers = row.jewellers || [];
        if (row.type === 'All') return 'All';
        if (!jewellers.length) return '-';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
              {jewellers[0].jewellerName}
              {jewellers.length > 1 ? ` +${jewellers.length - 1} more` : ''}
            </Typography>
            <button
              style={{
                backgroundColor: '#ffc107',
                color: '#000',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setJewellersList(jewellers);
                setJewellerSearch('');
                setJewellersOpen(true);
              }}
            >
              View
            </button>
          </Box>
        );
      },
    },
    {
      field: 'files',
      headerName: 'Files',
      minWidth: 120,
      hideInExcel: true,
      sortable: false,
      render: (val, row) =>
        row.files?.[0] ? (
          <button
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => {
              setImageUrl(row.files[0]);
              setImageOpen(true);
            }}
          >
            View
          </button>
        ) : (
          '-'
        ),
    },
    {
      field: 'statusText',
      headerName: 'Status',
      minWidth: 130,
      render: (status, row) => (
        <Button
          variant="contained"
          style={{
            backgroundColor: row.status ? 'green' : '#d32f2f',
            color: 'white',
            textTransform: 'none',
          }}
          onClick={() => handleStatusClick(row)}
        >
          {status}
        </Button>
      ),
    },
    { field: 'createdByName', headerName: 'Created By', minWidth: 150 },
    { field: 'updatedByName', headerName: 'Updated By', minWidth: 150 },
    {
      field: 'edit',
      headerName: 'Edit',
      minWidth: 100,
      hideInExcel: true,
      sortable: false,
      render: (val, row) => (
        <CiEdit
          style={{ cursor: 'pointer', fontSize: '22px' }}
          onClick={() => handleEdit(row)}
        />
      ),
    },
  ];

  const bannerForm = (
    <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value);
                if (e.target.value === 'All') setSelectedJewellers([]);
              }}
            >
              <FormControlLabel value="All" control={<Radio />} label="All" />
              <FormControlLabel
                value="Custom"
                control={<Radio />}
                label="Custom"
              />
            </RadioGroup>
          </FormControl>

          {targetType === 'Custom' && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={[
                ...jewellerOptions,
                ...selectedJewellers
                  .filter((id) => !jewellerOptions.some((item) => item.id === id))
                  .map((id) => {
                    const editRow = bannerData.find((item) => item._id === editId);
                    const fromRow = (editRow?.jewellers || []).find(
                      (item) => item._id === id,
                    );
                    return { id, jewellerName: fromRow?.jewellerName || id };
                  }),
              ]}
              getOptionLabel={(option) => option?.jewellerName || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedJewellers.map((id) => {
                const jeweller = jewellerOptions.find((item) => item.id === id);
                if (jeweller) return jeweller;
                const editRow = bannerData.find((item) => item._id === editId);
                const fromRow = (editRow?.jewellers || []).find(
                  (item) => item._id === id,
                );
                return { id, jewellerName: fromRow?.jewellerName || id };
              })}
              onChange={(_, newValue) =>
                setSelectedJewellers(newValue.map((item) => item.id))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Jeweller"
                  placeholder="Search jeweller"
                />
              )}
              sx={{ minWidth: 280, width: 320 }}
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
          <input
            type="file"
            accept="image/*"
            onClick={(e) => {
              e.target.value = null;
            }}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="lucky-draw-banner-image"
          />
          <label htmlFor="lucky-draw-banner-image">
            <Button variant="outlined" component="span">
              Choose File
            </Button>
          </label>
          <Typography variant="body2">
            {fileName ||
              (editId ? 'Choose a file only if you want to replace the image' : '')}
          </Typography>
        </Box>
        {(previewUrl || existingFile) && (
          <Box mt={2} mb={2}>
            <img
              src={previewUrl || resolveFileUrl(existingFile)}
              alt="Preview"
              style={{ width: 100, height: 'auto' }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: '#752ADA', px: 6, textTransform: 'none' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : editId ? 'Update' : 'Save'}
          </Button>
          {editId && (
            <Button
              variant="outlined"
              sx={{ px: 4, textTransform: 'none' }}
              onClick={resetForm}
            >
              Cancel
            </Button>
          )}
        </Box>
    </>
  );

  return (
    <Box sx={{ width: '100%', pr: 2, boxSizing: 'border-box' }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          width: '100%',
          border: '1px solid #ccc',
          boxSizing: 'border-box',
        }}
      >
        <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={'Aeonik'}>
          Lucky Draw Banner
        </Typography>
        {!editId && bannerForm}
      </Paper>

      <Box
        sx={{
          mt: 3,
          pb: 3,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <Typography fontWeight={700} fontSize={24} fontFamily={'Aeonik'}>
          Lucky Draw Banner list
        </Typography>
        <TableCompo
          title="Lucky Draw Banner list"
          columns={columns}
          data={tableRows}
          totalCount={total}
          page={page}
          limit={limit}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onTableChange={handleTableChange}
          extraButtons={
            <Button size="small" variant="outlined" onClick={handleDownloadCsv}>
              Download CSV
            </Button>
          }
        />
      </Box>

      <Dialog
        open={!!editId}
        onClose={resetForm}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: 24, fontFamily: 'Aeonik', pb: 0 }}
        >
          Lucky Draw Banner
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>{editId ? bannerForm : null}</DialogContent>
      </Dialog>

      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{' '}
            <b>{selectedRow?.status ? 'deactivate' : 'activate'}</b> this
            banner?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            variant="contained"
            sx={{ backgroundColor: '#d32f2f', color: 'white' }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{ backgroundColor: 'green', color: 'white' }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={jewellersOpen}
        onClose={() => setJewellersOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Jewellers ({jewellersList.length})</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            placeholder="Search jeweller"
            value={jewellerSearch}
            onChange={(e) => setJewellerSearch(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {jewellersList
              .filter((j) =>
                (j.jewellerName || '')
                  .toLowerCase()
                  .includes(jewellerSearch.toLowerCase()),
              )
              .map((j, index) => (
                <Typography
                  key={j._id}
                  variant="body2"
                  sx={{ py: 1, borderBottom: '1px solid #eee' }}
                >
                  {index + 1}. {j.jewellerName}
                </Typography>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJewellersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Banner Image</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <img
            src={imageUrl}
            alt="Banner"
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: 8,
              objectFit: 'contain',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LuckyDrawBanner;
